import React, { useEffect, useState } from "react";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import CustomDatePicker from "../../components/common/CustomDatePicker";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";

const API_BASE = "https://tyka.premierhostings.com/beta/api";
const today = dayjs().startOf("day");

const AddDiscountPage = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [menuCategories, setMenuCategories] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [targetType, setTargetType] = useState("category");
  const [categoryId, setCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [childCategoryId, setChildCategoryId] = useState("");
  const [discountType, setDiscountType] = useState("percentage");

  // Support common ID field names returned by the API.
  const getId = (item) =>
    item?.id ??
    item?.category_id ??
    item?.sub_category_id ??
    item?.subcategory_id ??
    item?.child_category_id ??
    "";

  // Support common name field names returned by the API.
  const getName = (item) =>
    item?.name ??
    item?.category_name ??
    item?.sub_category_name ??
    item?.subcategory_name ??
    item?.child_category_name ??
    item?.title ??
    "Unnamed";

  // Normalize common Laravel API response formats.
  const getMenuArray = (responseData) => {
    if (Array.isArray(responseData)) {
      return responseData;
    }

    if (Array.isArray(responseData?.data)) {
      return responseData.data;
    }

    if (Array.isArray(responseData?.data?.categories)) {
      return responseData.data.categories;
    }

    if (Array.isArray(responseData?.categories)) {
      return responseData.categories;
    }

    if (Array.isArray(responseData?.menu)) {
      return responseData.menu;
    }

    return [];
  };

  // Fetch the complete category hierarchy from the menu API.
  const fetchMenu = async () => {
    try {
      setLoadingOptions(true);

      const response = await axios.get(`${API_BASE}/menu`);

      const menuData = getMenuArray(response.data);

      setMenuCategories(menuData);
    } catch (error) {
      console.error("Menu API error:", error.response?.data || error);

      toast.error("Failed to load menu categories.");
      setMenuCategories([]);
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    fetchMenu();
  }, []);

  // Read subcategories directly from the selected category.
  const selectedCategory = menuCategories.find(
    (item) => String(getId(item)) === String(categoryId),
  );

  const subcategories =
    selectedCategory?.subcategories ??
    selectedCategory?.sub_categories ??
    selectedCategory?.subCategories ??
    [];

  // Read child categories directly from the selected subcategory.
  const selectedSubcategory = subcategories.find(
    (item) => String(getId(item)) === String(subcategoryId),
  );

  const childCategories =
    selectedSubcategory?.children ??
    selectedSubcategory?.child_categories ??
    selectedSubcategory?.childCategories ??
    [];

  const handleDiscountTypeChange = (e) => {
    const value = e.target.value;

    // Products only support percentage discounts
    if (targetType === "product" && value !== "percentage") {
      setDiscountType("percentage");
      return;
    }

    setDiscountType(value);
  };

  // Reset selected IDs when the target type changes.
  const handleTargetTypeChange = (e) => {
    const value = e.target.value;

    setTargetType(value);
    setCategoryId("");
    setSubcategoryId("");
    setChildCategoryId("");
    // Automatically switch to percentage for products
    if (value === "product") {
      setDiscountType("percentage");
    }
  };

  // Reset dependent selections when the category changes.
  const handleCategoryChange = (e) => {
    setCategoryId(e.target.value);
    setSubcategoryId("");
    setChildCategoryId("");
  };

  // Reset child category when the subcategory changes.
  const handleSubcategoryChange = (e) => {
    setSubcategoryId(e.target.value);
    setChildCategoryId("");
  };

  const handleAddDiscount = async (e) => {
    e.preventDefault();

    const form = e.currentTarget;

    const nameField = form.elements.namedItem("name");
    const typeField = form.elements.namedItem("type");
    const amountField = form.elements.namedItem("amount");
    const activeField = form.elements.namedItem("active");

    if (!nameField || !typeField || !amountField || !activeField) {
      console.error("Missing form field:", {
        nameField,
        typeField,
        amountField,
        activeField,
      });

      toast.error("Please check your form input fields.");
      return;
    }

    const name = nameField.value.trim();
    const type = targetType === "product" ? "percentage" : discountType;
    const amount = Number(amountField.value);

    // CustomDatePicker values come from React state.
    const formattedStartDate = startDate
      ? dayjs(startDate).format("YYYY-MM-DD")
      : "";

    const formattedEndDate = endDate ? dayjs(endDate).format("YYYY-MM-DD") : "";

    const active = activeField.checked ? 1 : 0;

    // Continue with your existing validation and API logic.

    // Basic validation
    if (!name) {
      toast.error("Please enter discount name.");
      return;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Please enter a valid discount amount.");
      return;
    }

    if (type === "percentage" && amount > 100) {
      toast.error("Percentage discount cannot exceed 100%.");
      return;
    }

    if (!startDate || !endDate) {
      toast.error("Please select both dates.");
      return;
    }

    if (endDate < startDate) {
      toast.error("End date must be on or after the start date.");
      return;
    }

    // Validate selected target
    if (targetType === "category" && !categoryId) {
      toast.error("Please select a category.");
      return;
    }

    if (targetType === "subcategory" && !subcategoryId) {
      toast.error("Please select a subcategory.");
      return;
    }

    if (targetType === "child_category" && !childCategoryId) {
      toast.error("Please select a child category.");
      return;
    }

    // Discount payload
    const newDiscount = {
      name,
      type,
      amount,
      start_date: startDate,
      end_date: endDate,
      active,
      target_type: targetType,

      category_id: ["category", "subcategory", "child_category"].includes(
        targetType,
      )
        ? Number(categoryId)
        : null,

      subcategory_id: ["subcategory", "child_category"].includes(targetType)
        ? Number(subcategoryId)
        : null,

      child_category_id:
        targetType === "child_category" ? Number(childCategoryId) : null,

      product_id: null,
    };

    try {
      setLoading(true);

      Swal.fire({
        title: "Saving Discount...",
        text: "Please wait while we save your discount.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        background: "#ffffff",
        color: "#dc2626",
        didOpen: () => Swal.showLoading(),
      });

      // STEP 1: Create the discount
      const discountResponse = await axios.post(
        `${API_BASE}/discounts`,
        newDiscount,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      // Get the newly created discount ID.
      // Adjust these paths if your API response uses a different structure.
      const discountId =
        discountResponse.data?.id ??
        discountResponse.data?.discount?.id ??
        discountResponse.data?.data?.id ??
        discountResponse.data?.data?.discount?.id;

      if (!discountId) {
        throw new Error(
          "Discount was created, but its ID was not returned. Please check the API response.",
        );
      }
      console.log("discountId", discountId);
      debugger;

      // STEP 2: Determine the applicable target ID
      let applicableId;

      switch (targetType) {
        case "category":
          applicableId = categoryId;
          break;

        case "subcategory":
          applicableId = subcategoryId;
          break;

        case "child_category":
          applicableId = childCategoryId;
          break;

        case "product":
          applicableId = "";
          break;

        default:
          throw new Error("Invalid discount target type.");
      }

      // STEP 3: Save the discount application
      const applicationPayload = {
        discount_id: Number(discountId),
        applicable_type: targetType,
        applicable_id: targetType === "product" ? "" : Number(applicableId),
      };

      await axios.post(
        `${API_BASE}/discount-applications`,
        applicationPayload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      // Both APIs succeeded
      Swal.close();

      await Swal.fire({
        icon: "success",
        title: "Discount Added",
        text: "Discount and its application have been saved successfully.",
        confirmButtonColor: "#dc2626",
      });

      navigate("/discounts");
    } catch (error) {
      Swal.close();

      console.error("Discount API error:", error.response?.data || error);

      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to save discount. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-8 rounded-lg shadow-md dark:bg-gray-800 dark:text-white">
      <h1 className="text-3xl font-semibold mb-6 border-b pb-2 text-red-600 dark:border-gray-700">
        Add New Discount
      </h1>

      <p className="mb-8 text-gray-600 dark:text-gray-300">
        Create a flat-rate or percentage discount for a product, category,
        subcategory, or child category.
      </p>

      <form onSubmit={handleAddDiscount} className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Discount Name */}
          <div>
            <label className="block mb-2 font-medium">Discount Name</label>

            <Input
              name="name"
              placeholder="Enter discount name"
              className="w-full dark:bg-gray-700"
            />
          </div>

          {/* Discount Type */}
          <div>
            <label className="block mb-2 font-medium">Discount Type</label>

            <select
              name="type"
              value={discountType}
              onChange={handleDiscountTypeChange}
              className="w-full border rounded-md p-2 dark:bg-gray-700"
            >
              <option value="percentage">Percentage (%)</option>

              {targetType !== "product" && (
                <option value="fixed">Fixed Amount (₹)</option>
              )}
            </select>
          </div>

          {/* Discount Amount */}
          <div>
            <label className="block mb-2 font-medium">Amount</label>

            <Input
              name="amount"
              type="number"
              min="0.01"
              step="0.01"
              placeholder="Enter amount"
            />
          </div>

          {/* Target Type */}
          <div>
            <label className="block mb-2 font-medium">Apply Discount To</label>

            <select
              value={targetType}
              onChange={handleTargetTypeChange}
              className="w-full border rounded-md p-2 dark:bg-gray-700"
            >
              <option value="product">Products</option>
              <option value="category">Categories</option>
              <option value="child_category">Child Categories</option>
            </select>
          </div>
        </div>

        {/* Products: text only, no product dropdown */}
        {targetType === "product" && (
          <div>
            <label className="block mb-2 font-medium">
              Selected Discount Target
            </label>

            <div className="w-full border rounded-md p-3 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-white">
              Products
            </div>
          </div>
        )}

        {/* Category selection */}
        {targetType !== "product" && (
          <>
            <div>
              <label className="block mb-2 font-medium">Select Category</label>

              <select
                value={categoryId}
                onChange={handleCategoryChange}
                disabled={loadingOptions}
                className="w-full border rounded-md p-2 dark:bg-gray-700"
              >
                <option value="">
                  {loadingOptions ? "Loading categories..." : "Select category"}
                </option>

                {menuCategories.map((item) => (
                  <option key={getId(item)} value={getId(item)}>
                    {getName(item)}
                  </option>
                ))}
              </select>
            </div>

            {/* Subcategory selection */}
            {["subcategory", "child_category"].includes(targetType) && (
              <div>
                <label className="block mb-2 font-medium">
                  Select Subcategory
                </label>

                <select
                  value={subcategoryId}
                  onChange={handleSubcategoryChange}
                  disabled={!categoryId}
                  className="w-full border rounded-md p-2 dark:bg-gray-700"
                >
                  <option value="">Select subcategory</option>

                  {subcategories.map((item) => (
                    <option key={getId(item)} value={getId(item)}>
                      {getName(item)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Child category selection */}
            {targetType === "child_category" && (
              <div>
                <label className="block mb-2 font-medium">
                  Select Child Category
                </label>

                <select
                  value={childCategoryId}
                  onChange={(e) => setChildCategoryId(e.target.value)}
                  disabled={!subcategoryId}
                  className="w-full border rounded-md p-2 dark:bg-gray-700"
                >
                  <option value="">Select child category</option>

                  {childCategories.map((item) => (
                    <option key={getId(item)} value={getId(item)}>
                      {getName(item)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Start Date</label>

            <CustomDatePicker
              name="startDate"
              label="Start Date"
              value={startDate}
              onChange={(value) => setStartDate(value)}
              minDate={today}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">End Date</label>

            <CustomDatePicker
              name="endDate"
              label="End Date"
              value={endDate}
              onChange={(value) => setEndDate(value)}
              minDate={today}
            />
          </div>
        </div>

        {/* Active */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="active"
            name="active"
            defaultChecked
            className="h-5 w-5 accent-red-600"
          />

          <label htmlFor="active" className="font-medium">
            Active
          </label>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-4 border-t pt-4 dark:border-gray-700">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/discounts")}
          >
            Cancel
          </Button>

          <Button type="submit" disabled={loading}>
            {loading ? "Saving..." : "Add Discount"}
          </Button>
        </div>
      </form>

      <ToastContainer position="top-right" />
    </div>
  );
};

export default AddDiscountPage;
